import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { TextField } from "@/shared/components/Form/TextField";
import { ColorPickerField } from "@/shared/components/Form/ColorPickerField";
import { AutocompleteField } from "@/shared/components/Form/AutocompleteField";
import { Switch } from "@/shared/components/Form/Switch";
import type {
  PlatformBlockComponent,
  PlatformBlockProps,
} from "../../../types";
import { BlockFormHeader } from "../BlockFormHeader";

const buttonColorValues = ["primary", "secondary", "default"] as const;

const buttonVariantValues = ["default", "contained", "outlined"] as const;

const buttonSizeValues = ["small", "medium", "large"] as const;

const alignValues = ["left", "center", "right", "justify"] as const;

export const Button: PlatformBlockComponent = ({
  name,
}: PlatformBlockProps) => {
  const { t } = useTranslation();

  const buttonColorOptions = buttonColorValues.map((value) => ({
    value,
    label: t(`platformBlocks.buttonColor.${value}`),
  }));

  const buttonVariantOptions = buttonVariantValues.map((value) => ({
    value,
    label: t(`platformBlocks.buttonVariant.${value}`),
  }));

  const buttonSizeOptions = buttonSizeValues.map((value) => ({
    value,
    label: t(`platformBlocks.buttonSize.${value}`),
  }));

  const alignOptions = alignValues.map((value) => ({
    value,
    label: t(`platformBlocks.align.${value}`),
  }));

  return (
    <Paper sx={{ p: 2 }}>
      <BlockFormHeader blockType="BUTTON" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name={`${name}.text`}
            label={t("platformBlocks.fields.text")}
            type="text"
            fullWidth
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name={`${name}.url`}
            label="URL"
            type="text"
            fullWidth
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutocompleteField
            name={`${name}.variant`}
            label={t("platformBlocks.fields.variant")}
            fullWidth
            options={buttonVariantOptions}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutocompleteField
            name={`${name}.size`}
            label={t("platformBlocks.fields.size")}
            fullWidth
            options={buttonSizeOptions}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutocompleteField
            name={`${name}.color`}
            label={t("platformBlocks.fields.color")}
            fullWidth
            options={buttonColorOptions}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPickerField
            name={`${name}.backgroundColor`}
            label={t("platformBlocks.fields.backgroundColor")}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutocompleteField
            name={`${name}.align`}
            label={t("platformBlocks.fields.align")}
            fullWidth
            options={alignOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name={`${name}.maxWidth`}
            label={t("platformBlocks.fields.maxWidthShort")}
            type="number"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name={`${name}.paddingTop`}
            label={t("platformBlocks.fields.paddingTop")}
            type="number"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name={`${name}.paddingBottom`}
            label={t("platformBlocks.fields.paddingBottom")}
            type="number"
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <Switch
            name={`${name}.fullWidth`}
            label={t("platformBlocks.fields.fullWidth")}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
